(() => {
  const CAROUSEL_SELECTOR =
    'div.resource-list__carousel > slideshow-component.resource-list__carousel';

  const desktopQuery = window.matchMedia('(min-width: 750px)');
  const reducedMotionQuery = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  );

  const EASING_SPEED = 12;

  const instances = new Set();

  let currentPageScroll = window.scrollY;
  let animationFrame = null;
  let measurementFrame = null;
  let previousFrameTime = performance.now();

  function clamp(value, minimum, maximum) {
    return Math.min(Math.max(value, minimum), maximum);
  }

  function isEnabled() {
    return desktopQuery.matches && !reducedMotionQuery.matches;
  }

  function getStickyOffset(wrapper) {
    const styles = window.getComputedStyle(wrapper);

    return (
      parseFloat(
        styles.getPropertyValue('--resource-sticky-offset')
      ) || 120
    );
  }

  function resetInstance(instance) {
    instance.wrapper.style.removeProperty('height');

    instance.maximumScroll = 0;
    instance.currentScroll = instance.scroller.scrollLeft;
    instance.targetScroll = instance.currentScroll;
  }

  function measureInstance(instance) {
    if (!instance.carousel.isConnected) return;

    if (!isEnabled()) {
      resetInstance(instance);
      return;
    }

    const maximumScroll = Math.max(
      0,
      instance.scroller.scrollWidth -
        instance.scroller.clientWidth
    );

    instance.maximumScroll = maximumScroll;

    if (maximumScroll <= 0) {
      resetInstance(instance);
      return;
    }

    const stickyOffset = getStickyOffset(instance.wrapper);

    /*
     * Absolute page position where horizontal scrolling begins.
     * This avoids calling getBoundingClientRect() on every scroll.
     */
    instance.startPosition =
      instance.wrapper.getBoundingClientRect().top +
      window.scrollY -
      stickyOffset;

    /*
     * Gives the sticky carousel enough page height to complete
     * the full horizontal movement.
     */
    instance.wrapper.style.height = `${
      instance.carousel.offsetHeight + maximumScroll
    }px`;

    instance.targetScroll = clamp(
      currentPageScroll - instance.startPosition,
      0,
      maximumScroll
    );

    /*
     * Prevent an animation from zero when loading midway
     * down the page or using the Shopify theme editor.
     */
    if (!instance.hasMeasured) {
      instance.hasMeasured = true;
      instance.currentScroll = instance.targetScroll;
      instance.scroller.scrollLeft = instance.targetScroll;
    }
  }

  function measureAll() {
    measurementFrame = null;
    currentPageScroll = window.scrollY;

    instances.forEach((instance) => {
      if (!instance.carousel.isConnected) {
        instances.delete(instance);
        return;
      }

      measureInstance(instance);
    });

    requestAnimation();
  }

  function requestMeasurement() {
    if (measurementFrame !== null) return;

    measurementFrame = requestAnimationFrame(measureAll);
  }

  function animate(timestamp) {
    animationFrame = null;

    const deltaTime = Math.min(
      (timestamp - previousFrameTime) / 1000,
      0.05
    );

    previousFrameTime = timestamp;

    /*
     * Time-based easing keeps the movement consistent on
     * 60 Hz, 120 Hz and other refresh rates.
     */
    const easingAmount =
      1 - Math.exp(-EASING_SPEED * deltaTime);

    let continueAnimation = false;

    instances.forEach((instance) => {
      if (
        !instance.carousel.isConnected ||
        !isEnabled() ||
        instance.maximumScroll <= 0
      ) {
        return;
      }

      instance.targetScroll = clamp(
        currentPageScroll - instance.startPosition,
        0,
        instance.maximumScroll
      );

      const difference =
        instance.targetScroll - instance.currentScroll;

      if (Math.abs(difference) < 0.25) {
        instance.currentScroll = instance.targetScroll;
      } else {
        instance.currentScroll += difference * easingAmount;
        continueAnimation = true;
      }

      instance.scroller.scrollLeft =
        instance.currentScroll;
    });

    if (continueAnimation) {
      animationFrame = requestAnimationFrame(animate);
    }
  }

  function requestAnimation() {
    if (animationFrame !== null) return;

    previousFrameTime = performance.now();
    animationFrame = requestAnimationFrame(animate);
  }

  function handlePageScroll() {
    currentPageScroll = window.scrollY;
    requestAnimation();
  }

  function initialiseCarousels(container = document) {
    const carousels = [];

    if (
      container.matches &&
      container.matches(CAROUSEL_SELECTOR)
    ) {
      carousels.push(container);
    }

    if (container.querySelectorAll) {
      carousels.push(
        ...container.querySelectorAll(CAROUSEL_SELECTOR)
      );
    }

    carousels.forEach((carousel) => {
      if (
        carousel.dataset.verticalScrollReady === 'true'
      ) {
        return;
      }

      const wrapper = carousel.parentElement;
      const scroller = carousel.querySelector(
        'slideshow-slides'
      );

      if (!wrapper || !scroller) return;

      carousel.dataset.verticalScrollReady = 'true';
      wrapper.classList.add(
        'resource-list-scroll-wrapper'
      );

      const instance = {
        carousel,
        wrapper,
        scroller,
        maximumScroll: 0,
        startPosition: 0,
        currentScroll: scroller.scrollLeft,
        targetScroll: scroller.scrollLeft,
        hasMeasured: false
      };

      instances.add(instance);

      /*
       * Recalculate if the carousel dimensions change.
       */
      resizeObserver.observe(carousel);
      resizeObserver.observe(scroller);

      /*
       * Images may affect dimensions after initial rendering.
       */
      scroller.querySelectorAll('img').forEach((image) => {
        if (!image.complete) {
          image.addEventListener(
            'load',
            requestMeasurement,
            { once: true }
          );
        }
      });
    });

    requestMeasurement();
  }

  const resizeObserver = new ResizeObserver(() => {
    requestMeasurement();
  });

  window.addEventListener(
    'scroll',
    handlePageScroll,
    { passive: true }
  );

  window.addEventListener(
    'resize',
    requestMeasurement,
    { passive: true }
  );

  desktopQuery.addEventListener(
    'change',
    requestMeasurement
  );

  reducedMotionQuery.addEventListener(
    'change',
    requestMeasurement
  );

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => initialiseCarousels(),
      { once: true }
    );
  } else {
    initialiseCarousels();
  }

  window.addEventListener(
    'load',
    requestMeasurement,
    { once: true }
  );

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initialiseCarousels(event.target);
    }
  );

  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      instances.forEach((instance) => {
        if (event.target.contains(instance.carousel)) {
          resizeObserver.unobserve(instance.carousel);
          resizeObserver.unobserve(instance.scroller);
          instances.delete(instance);
        }
      });
    }
  );
})();